import React from "react";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { url } from "src/api";
import { useAnketaI18n } from "src/internationalization";
import { HoverImageCard } from "./image";

const C = {
  accent: "var(--ak-accent)",
  accentLight: "var(--ak-accent-tint)",
  accentBorder: "var(--ak-accent-border)",
  pageBg: "var(--ak-page-bg)",
  cardBg: "var(--ak-surface)",
  subtleBg: "var(--ak-surface-2)",
  subtleBorder: "var(--ak-surface-3)",
  border: "var(--ak-border)",
  textPrimary: "var(--ak-text)",
  textSecondary: "var(--ak-text-secondary)",
  textMuted: "var(--ak-text-muted)",
  sans: "system-ui,-apple-system,sans-serif",
  radius: 12,
};

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      fontSize: "0.7rem",
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: C.accent,
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);

const AccentUnderline: React.FC<{ width?: number | string }> = ({ width = "100%" }) => (
  <div
    style={{
      width,
      height: 3,
      background: `linear-gradient(90deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
      borderRadius: 2,
      marginTop: 14,
      marginBottom: 28,
    }}
  />
);

interface LangContent {
  intro: React.ReactNode;
}

// Deliberately compact - this is a product page, so the company stays in the
// background. We keep a short, honest "who operates opsanio" note (founded
// 2023, Hamburg, team across DE/AT/HR) plus the team photo + facts card, and
// drop the long origin story / capability list that used to live here.
const CONTENT: Record<"de" | "en", LangContent> = {
  de: {
    intro: (
      <>
        <p>
          <strong style={{ color: C.accent }}>opsanio</strong> wird von der{" "}
          <strong style={{ color: C.accent }}>autonubil GmbH</strong> entwickelt
          und als Managed Appliance betrieben - einem 2023 in Hamburg neu
          gestarteten Team mit Spezialisten in Deutschland, Österreich und
          Kroatien.
        </p>
        <p>
          Aus jahrelanger Erfahrung in der{" "}
          <strong>Automatisierung von Systemen, Diensten und Applikationen</strong>{" "}
          - stets nach dem KISS-Prinzip - entstand eine Plattform, die Sicherheit,
          Transparenz und Kontrolle über Ihre Infrastruktur in einer souveränen
          Lösung vereint.
        </p>
      </>
    ),
  },

  en: {
    intro: (
      <>
        <p>
          <strong style={{ color: C.accent }}>opsanio</strong> is built and
          operated as a managed appliance by{" "}
          <strong style={{ color: C.accent }}>autonubil GmbH</strong> - a team
          relaunched in Hamburg in 2023, with specialists across Germany, Austria
          and Croatia.
        </p>
        <p>
          Years of experience{" "}
          <strong>automating systems, services and applications</strong> - always
          following the KISS principle - produced a platform that unifies
          security, transparency and control over your infrastructure in one
          sovereign solution.
        </p>
      </>
    ),
  },
};

export const AutonubilAbout: React.FC = () => {
  const i18nPrefix = "anketa.autonubil.home.about";
  const { translate, currentLang } = useAnketaI18n();

  const lang = currentLang === "de" ? "de" : "en";
  const content = CONTENT[lang];

  return (
    <div
      id="about"
      style={{
        background: C.pageBg,
        padding: "72px clamp(24px, 5vw, 48px)",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Eyebrow>
          {translate(`${i18nPrefix}.eyebrow`, {
            defaultMessage: "About us",
          } as any)}
        </Eyebrow>
        <div style={{ width: "fit-content", maxWidth: "100%" }}>
          <EuiTitle size="l">
            <h2
              style={{
                color: C.textPrimary,
                fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
                marginBottom: 0,
              }}
            >
              {translate(`${i18nPrefix}.title`)}
            </h2>
          </EuiTitle>
          <AccentUnderline />
        </div>

        <EuiFlexGroup gutterSize="xl" alignItems="flexStart" responsive wrap>
          <EuiFlexItem style={{ minWidth: "min(100%, 520px)" }}>
            <div
              style={{
                background: C.cardBg,
                border: `1px solid ${C.border}`,
                borderRadius: C.radius,
                padding: "clamp(20px, 4vw, 36px)",
                boxShadow: "0 1px 4px var(--ak-shadow)",
              }}
            >
              <EuiText>
                <div
                  style={{
                    color: C.textSecondary,
                    lineHeight: 1.75,
                    fontSize: "clamp(0.92rem, 1.8vw, 1rem)",
                  }}
                >
                  {content.intro}
                </div>
              </EuiText>
            </div>
          </EuiFlexItem>

          <EuiFlexItem
            grow={false}
            style={{ flexBasis: "min(100%, 440px)", margin: "0 auto" }}
          >
            <div
              style={{
                position: "sticky",
                top: 24,
                borderRadius: C.radius,
                overflow: "hidden",
                border: `1px solid ${C.border}`,
                boxShadow:
                  "0 8px 32px var(--ak-shadow-strong), 0 2px 8px var(--ak-shadow)",
              }}
            >
              <HoverImageCard
                imageUrl={`${url}v2/images/autonubil-team.png`}
                alt={translate(`${i18nPrefix}.image.alt`)}
                title={translate(`${i18nPrefix}.image.title`)}
                description={translate(`${i18nPrefix}.image.description`)}
              />
            </div>

            <div
              style={{
                display: "flex",
                marginTop: 16,
                background: C.cardBg,
                border: `1px solid ${C.border}`,
                borderRadius: C.radius,
                overflow: "hidden",
                boxShadow: "0 1px 4px var(--ak-shadow)",
              }}
            >
              {[
                { num: "2023", label: lang === "de" ? "Gegründet" : "Founded" },
                { num: "3", label: lang === "de" ? "Länder" : "Countries" },
                { num: "DE", label: "Hamburg HQ" },
              ].map(({ num, label }, i, arr) => (
                <div
                  key={label}
                  style={{
                    flex: 1,
                    padding: "14px 10px",
                    textAlign: "center",
                    borderRight:
                      i < arr.length - 1 ? `1px solid ${C.border}` : "none",
                  }}
                >
                  <div
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: 800,
                      color: C.accent,
                      lineHeight: 1,
                    }}
                  >
                    {num}
                  </div>
                  <div
                    style={{
                      fontSize: "0.67rem",
                      color: C.textSecondary,
                      marginTop: 4,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </EuiFlexItem>
        </EuiFlexGroup>
      </div>
    </div>
  );
};

export default AutonubilAbout;
